<script>
import * as d3 from 'd3'
import D3Cloud from 'd3-cloud'

import stop_words from '@/assets/data/stopwords.json'
import CandelaComponent from '@/components/CandelaComponent'

export default {
  extends: CandelaComponent,
  name: 'candela-word-cloud',
  computed: {
    cloud() {
      const min = Number(this.settings.angle_range.value[0])
      const max = Number(this.settings.angle_range.value[1])
      const count = Number(this.settings.angle_count.value)
      return this.layout
        .font('DMSans')
        .fontSize(d => this.font_scale(d.count))
        .fontWeight('bold')
        .rotate(d => {
          const r = Math.floor(Math.random() * (count))
          const i = ((max - min) / Math.max(1, (count - 1)))
          const a = min + r * i
          return a
        })
        .size([this.width, this.height])
        .spiral(this.settings.spiral.value)
        .words(this.words.map(w => ({...w})))
    },
    color_scale() {
      return d3.scaleSqrt()
        .domain([this.min_count, this.max_count])
        .range([0, 1])
    },
    font_max() {
      return d3.scaleLinear()
        .domain([0, 4096])
        .range([32, 480])
    },
    font_min() {
      return d3.scaleLinear()
        .domain([0, 2048])
        .range([8, 32])
    },
    font_scale() {
      const s = Math.min(this.width, this.height)
      return d3.scaleLog()
        .domain([this.min_count, this.max_count])
        .range([this.font_min(s), this.font_max(s)])
    },
    max_count() {
      return this.words.reduce((acc, w) => Math.max(acc, w.count), -Infinity)
    },
    min_count() {
      return this.words.reduce((acc, w) => Math.min(acc, w.count), Infinity)
    },
    words() {
      return Object.values(
        this.text
          .toLowerCase()
          .replace(/[^a-z0-9]/gi, ' ')
          .split(' ')
          .reduce((acc, w) => {
            if(!acc[w] &&
              w.length > 1 &&
              !stop_words.includes(w)) {
              acc[w] = {count: 0, text: w}
            }
            if(acc[w]) acc[w].count++
            return acc
          }, {}))
        .splice(0, this.count)
    }
  },
  data() {
    return {
      cloud_data: [],
      frame_timeout: null,
      height: 0,
      layout: D3Cloud(),
      layout_timeout: null,
      width: 0
    }
  },
  methods: {
    update(data) {
      this.cloud_data = data
    }
  },
  mounted() {
    setTimeout(() => {
      this.height = this.$refs.container.clientHeight
      this.width = this.$refs.container.clientWidth
    }, 250)
  },
  props: {
    count: {type: Number, default: () => 64},
    text: {type: String, default: () => ''}
  },
  watch: {
    cloud(curr, prev) {
      if(prev) {
        prev.stop()
        prev = null
      }
      clearTimeout(this.layout_timeout)
      this.layout_timeout = setTimeout(() => {
        this.cloud
          .on('end', this.update.bind(this))
          .start()
      }, 250)
    },
    frame() {
      if(this.$refs.container) {
        const h = this.$refs.container.clientHeight
        const w = this.$refs.container.clientWidth
        if(this.height !== h || this.width !== w) {
          this.cloud.stop()
          if(!this.frame_timeout) {
            clearTimeout(this.frame_timeout)
            this.frame_timeout = setTimeout(() => {
              this.frame_timeout = null
              this.height = this.$refs.container.clientHeight
              this.width = this.$refs.container.clientWidth
            }, 1200)
          }
        }
      }
    }
  }
}
</script>

<template>
<div ref='container' class='candela-component d-flex align-center justify-center bg'
  :style='{backgroundColor: settings.background.value}'>
  <svg width='100%' height='100%'>
    <text v-for='w in cloud_data' :key='w.text' i
    font-family='DMSans' :font-size='`${w.size}px`' font-weight='bold' class='word'
    :transform='`translate(${width * 0.5 + w.x}, ${height * 0.5 + w.y}) rotate(${w.rotate})`'
    :fill='settings.colors.value(color_scale(w.count))' text-anchor='middle'>
      {{w.text}}
    </text>
  </svg>
</div>
</template>

<style lang='sass' scoped>
.bg
  transition: background-color 0.2s
.word
  transition: all 0.2s
</style>
